// Setup › Memory & privacy (`/c/{slug}/setup/memory-privacy`; owners and admins, the `memory`
// add-on): retention and its monthly dry run, legal holds, pins against cooling, what the company
// asked the operator to do in Recall, open forget requests, and the audit export.
// RECALL-RETENTION-HOLD-FORGET: any action the memory service does not offer yet reads "Waiting on
// memory service" and never errors. A hold's release and a legal contact are signed-in-only in
// Recall, so they go to the operator as a request; this screen never claims they are done.

import type { OperationOutput } from "@companyos/sdk";
import { Badge, Button, SettingsGroup, SettingsRow } from "@companyos/ui";
import { queryOptions, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { ReactNode } from "react";
import type { Api } from "../../api/client";
import type { CompanyAccess } from "../company";
import { formatDateTime } from "../format";
import { LoadError, Loading } from "../screen-parts";
import { problemText } from "../settings/problem-text";
import { SetupPage } from "../settings/setup-page";

type Gov = OperationOutput<"memoryGov.get">;

const govKey = (co: string) => ["company", co, "memory", "governance"] as const;

export const memoryGovQuery = (api: Api, co: string) =>
  queryOptions({
    queryKey: govKey(co),
    queryFn: () => api.client.memoryGov.get({ params: { co } }),
  });

const HOLD_STATE: Record<
  Gov["holds"][number]["state"],
  [string, "success" | "warning" | "danger"]
> = {
  waiting: ["Waiting on memory service", "warning"],
  placed: ["In force", "danger"],
  release_requested: ["Release asked", "warning"],
  released: ["Released", "success"],
};

export function MemoryPrivacyScreen({ api, company }: { api: Api; company: CompanyAccess }) {
  const q = useQuery(memoryGovQuery(api, company.id));
  const qc = useQueryClient();
  const co = company.id;
  const act = useMutation({
    mutationFn: (body: Parameters<Api["client"]["memoryGov"]["act"]>[0]["body"]) =>
      api.client.memoryGov.act({ params: { co }, body }),
    onSettled: () => void qc.invalidateQueries({ queryKey: govKey(co) }),
  });
  return (
    <SetupPage title="Memory & privacy">
      {q.isPending ? (
        <Loading label="Loading memory & privacy" rows={4} />
      ) : q.isError ? (
        <LoadError what="memory & privacy" error={q.error} />
      ) : (
        <View
          gov={q.data}
          co={co}
          api={api}
          onRelease={(id) => act.mutate({ action: "hold_release", hold_id: id })}
          onUnkeep={(id) => act.mutate({ action: "unkeep", keep_id: id })}
          notice={act.data?.notice ?? act.data?.waiting ?? null}
        />
      )}
    </SetupPage>
  );
}

/** Fetches the audit file and saves it; a refusal (a secret in a line, a down service) is shown. */
function AuditExport({ api, co }: { api: Api; co: string }) {
  const save = useMutation({
    mutationFn: async () => {
      const blob = await api.client.memoryGov.auditExport({ params: { co }, query: {} });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `memory-audit-${new Date().toISOString().slice(0, 10)}.json`;
      a.click();
      URL.revokeObjectURL(url);
    },
  });
  return (
    <span className="flex items-center gap-2">
      {save.isError ? (
        <span role="alert" className="text-[13px] text-destructive">
          {problemText(save.error, "The audit could not be exported")}
        </span>
      ) : null}
      <Button size="sm" variant="outline" disabled={save.isPending} onClick={() => save.mutate()}>
        Export
      </Button>
    </span>
  );
}

function Row({ label, control }: { label: string; control: ReactNode }) {
  return <SettingsRow label={label}>{control}</SettingsRow>;
}

function View({
  gov,
  co,
  api,
  onRelease,
  onUnkeep,
  notice,
}: {
  gov: Gov;
  co: string;
  api: Api;
  onRelease: (id: string) => void;
  onUnkeep: (id: string) => void;
  notice: string | null;
}) {
  const waiting = gov.capabilities.waiting_text;
  const r = gov.retention;
  return (
    <>
      {notice ? <p className="px-1 text-[13px] text-muted-foreground">{notice}</p> : null}
      {gov.hold_banner.active ? (
        <p role="status" className="px-1 text-[13px]">
          A legal hold stands ({gov.hold_banner.case_refs.join(", ")}). Forgets wait and the company
          cannot be archived or deleted.
        </p>
      ) : null}
      <SettingsGroup title="Retention" data-slot="memory-retention">
        <Row
          label="Cool after"
          control={
            r ? (
              <span className="text-[13px]">
                {r.policy.cool_days} days · scratch {r.policy.scratch_days} days
              </span>
            ) : (
              <Badge variant="warning">{waiting}</Badge>
            )
          }
        />
        <Row
          label="Monthly dry run"
          control={
            r?.last_dry_run ? (
              <span className="text-[13px]">
                {r.last_dry_run.period}: {r.last_dry_run.total} would cool
              </span>
            ) : (
              <span className="text-[13px] text-muted-foreground">
                {gov.capabilities.retention ? "None yet" : waiting}
              </span>
            )
          }
        />
      </SettingsGroup>
      <SettingsGroup title="Legal holds" count={gov.holds.length} data-slot="memory-holds">
        {gov.holds.length === 0 ? (
          <p className="px-4 py-3 text-[13px] text-muted-foreground">No holds.</p>
        ) : (
          gov.holds.map((h) => {
            const [label, tone] = HOLD_STATE[h.state];
            return (
              <Row
                key={h.id}
                label={`${h.case_ref} · ${h.scope_name ?? "whole workspace"}`}
                control={
                  <span className="flex items-center gap-2">
                    <Badge variant={tone}>{label}</Badge>
                    {h.state === "placed" ? (
                      <Button size="sm" variant="outline" onClick={() => onRelease(h.id)}>
                        Ask operator to release
                      </Button>
                    ) : null}
                  </span>
                }
              />
            );
          })
        )}
      </SettingsGroup>
      <SettingsGroup title="Kept from cooling" count={gov.keeps.length} data-slot="memory-keeps">
        {gov.keeps.length === 0 ? (
          <p className="px-4 py-3 text-[13px] text-muted-foreground">Nothing pinned.</p>
        ) : (
          gov.keeps.map((k) => (
            <Row
              key={k.id}
              label={`${k.space_name ?? "Space"}${k.record_title ? ` · ${k.record_title}` : ""}`}
              control={
                <span className="flex items-center gap-2">
                  {k.sent ? null : <Badge variant="warning">{waiting}</Badge>}
                  <Button size="sm" variant="outline" onClick={() => onUnkeep(k.id)}>
                    Unpin
                  </Button>
                </span>
              }
            />
          ))
        )}
      </SettingsGroup>
      <SettingsGroup title="Asked of the operator" count={gov.jobs.length} data-slot="memory-jobs">
        {gov.jobs.length === 0 ? (
          <p className="px-4 py-3 text-[13px] text-muted-foreground">Nothing asked.</p>
        ) : (
          gov.jobs.map((j) => (
            <Row
              key={j.id}
              label={j.kind === "hold_release" ? "Release a hold" : "Name a legal contact"}
              control={
                <Badge variant={j.state === "open" ? "warning" : "success"}>
                  {j.state === "open"
                    ? `Open since ${formatDateTime(j.created_at)}`
                    : `Done ${j.closed_at ? formatDateTime(j.closed_at) : ""}`}
                </Badge>
              }
            />
          ))
        )}
      </SettingsGroup>
      <SettingsGroup title="Forget requests" count={gov.forgets.length} data-slot="memory-forgets">
        {gov.forgets.length === 0 ? (
          <p className="px-4 py-3 text-[13px] text-muted-foreground">None open.</p>
        ) : (
          gov.forgets.map((f) => (
            <Row
              key={f.id}
              label={f.label}
              control={
                <Badge
                  variant={f.state === "waiting" || f.state === "blocked" ? "warning" : "neutral"}
                >
                  {f.state === "waiting"
                    ? waiting
                    : f.state === "blocked"
                      ? `On hold${f.case_ref ? ` (${f.case_ref})` : ""}`
                      : f.state}
                </Badge>
              }
            />
          ))
        )}
      </SettingsGroup>
      <SettingsGroup title="Audit" data-slot="memory-audit">
        <Row label="Export the memory audit" control={<AuditExport api={api} co={co} />} />
      </SettingsGroup>
    </>
  );
}
